import { PACKING_QUEUE_MAX_LIMIT } from "@loot-os/fulfillment-core/order/packing-fold";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import { Switch } from "@loot-os/ui/components/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { formatNumber } from "@loot-os/ui/lib/format";
import { Check } from "lucide-react";
import { useState } from "react";
import { dashIfEmpty } from "../../lib/format";
import { TAG_TONES } from "../../lib/fulfillmentOrders";
import { OPEN_IN_TABS_LABEL } from "../../lib/orderTabs";
import { type Dateish, PACKING_TABS, toDate } from "../../lib/packing";
import {
  type GroupedMissingCardView,
  type MissingCardLineView,
  MissingCards,
} from "./MissingCards";
import { OrderReference } from "./OrderReference";

/** Retool's columns, in Retool's order. No value, destination or tenant column — it has none. */
const COLUMNS = [
  "Created at",
  "Name",
  "SubStatus",
  "Updated at",
  "Is in stock",
  "Item Count",
  "Categories",
  "ID",
  "Slow Ship",
] as const;

/**
 * Retool colours the Categories pill per game, which is how a packer tells a Pokémon bag from a
 * Magic one at a glance. The page follows the dashboard theme, so each colour has a `dark:` value.
 */
const CATEGORY_BADGE: Record<string, string> = {
  POKEMON: TAG_TONES.green,
  MTG: TAG_TONES.violet,
  LORCANA: TAG_TONES.amber,
  ONE_PIECE: TAG_TONES.red,
  RIFTBOUND: TAG_TONES.blue,
};
const CATEGORY_BADGE_FALLBACK = "bg-muted text-muted-foreground";

const SUB_STATUS_BADGE = "bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-300";

const SUB_STATUS_LABEL: Record<string, string> = {
  NOT_PACKED: "NOT PACKED",
  PARTIALLY_PACKED: "PARTIAL PACKED",
  READY_TO_SHIP: "READY TO SHIP",
};

/** What this table renders, declared here so a tRPC-serialised date (a string) fits. */
export interface ParityQueueRow {
  orderId: string;
  type: string;
  tenantReferenceId: string | null;
  /** A withdrawal whose shipping fee loot-os has not charged yet: flagged and never selectable. */
  shopperName: string | null;
  subStatus: string;
  createdAt: Dateish;
  updatedAt: Dateish;
  lineCount: number;
  categories: string[];
  inStock: boolean;
  isSlowShip: boolean;
}

function when(value: Dateish): string {
  return toDate(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * A number field you can actually empty.
 *
 * A controlled `<input type="number">` that commits `Number(value) || fallback` on every
 * keystroke cannot be cleared: deleting the last digit sends "" and the fallback puts a digit
 * straight back, so a packer has to use the steppers instead of typing. The draft string lives
 * here, and only a parseable value is committed; leaving the field empty restores the last good
 * one on blur.
 */
function NumberField({
  id,
  label,
  value,
  min,
  max,
  onCommit,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max?: number;
  onCommit: (next: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <label className="flex flex-col gap-1 text-xs text-muted-foreground" htmlFor={id}>
      {label}
      <Input
        id={id}
        className="w-24"
        type="number"
        min={min}
        max={max}
        value={draft ?? String(value)}
        onChange={(event) => {
          const next = event.target.value;
          setDraft(next);
          if (next === "") return;
          const parsed = Number(next);
          if (!Number.isNaN(parsed) && parsed >= min && (max === undefined || parsed <= max)) {
            onCommit(parsed);
          }
        }}
        onBlur={() => setDraft(null)}
      />
    </label>
  );
}

export interface ParityQueueProps {
  data: { rows: ParityQueueRow[]; total: number } | undefined;
  isPending: boolean;
  /** How many orders the checkbox takes — the packer's assignment size. */
  batch: number;

  offset: number;
  /** Weeks; 0 — the default — has every waiting bag reserve the stock it needs. */
  slaWeeks: number;
  /** The scan hit its cap, so the total is a floor and the stock answer past it is approximate. */
  scanTruncated: boolean;
  category: string | undefined;
  categories: string[];
  includeOutOfStock: boolean;
  batchSelected: boolean;
  /** Every row currently shown is selected — what the header checkbox reflects. */
  allShownSelected: boolean;
  /** Ids currently selected, so each row can show its own state. */
  selection: string[];
  tab: string;
  from: string | undefined;
  to: string | undefined;
  onToggleRow: (orderId: string) => void;
  onTab: (tab: "unpacked" | "partial" | "missing") => void;
  onFrom: (value: string | undefined) => void;
  onTo: (value: string | undefined) => void;
  onOpenInTabs: () => void;
  onExportSlips: () => void;
  /** The order list's Export Pull Sheet over the selected bags. */
  onExportPullSheet: () => void;
  onGenerateMissingCards: () => void;
  /** Short lines from the missing-card walk. Shown on the Missing Cards tab, and printed from it. */
  missingCards:
    | { lines: MissingCardLineView[]; grouped: GroupedMissingCardView[]; scanTruncated: boolean }
    | undefined;
  missingCardsPending: boolean;
  missingCardsFailed: boolean;
  showAllMissing: boolean;
  includeLockedAccounts: boolean;
  missingFrom: string | undefined;
  missingTo: string | undefined;
  onShowAllMissing: (value: boolean) => void;
  onIncludeLockedAccounts: (value: boolean) => void;
  onMissingFrom: (value: string | undefined) => void;
  onMissingTo: (value: string | undefined) => void;
  onBatch: (batch: number) => void;
  onOffset: (offset: number) => void;
  onSlaWeeks: (weeks: number) => void;
  onCategory: (category: string | undefined) => void;
  onIncludeOutOfStock: (include: boolean) => void;
  onSelectBatch: (selected: boolean) => void;
  onToggleAllShown: () => void;
  onOpenCards: () => void;
}

/**
 * The queue as *Fulfillment/TCG Pack Orders* lays it out. Kept deliberately close: this is a
 * daily tool for a dozen packers, so an unnecessary difference costs them more than it gains us.
 *
 * The batch model is Retool's: `# of Orders` and `Offset` decide the window, and one checkbox
 * takes the whole window — there is no per-row ticking.
 */
export function ParityQueue({
  data,
  isPending,
  batch,
  offset,
  slaWeeks,
  scanTruncated,
  category,
  categories,
  includeOutOfStock,
  batchSelected,
  allShownSelected,
  selection,
  tab,
  from,
  to,
  onToggleRow,
  onTab,
  onFrom,
  onTo,
  onOpenInTabs,
  onExportSlips,
  onExportPullSheet,
  onGenerateMissingCards,
  missingCards,
  missingCardsPending,
  missingCardsFailed,
  showAllMissing,
  includeLockedAccounts,
  missingFrom,
  missingTo,
  onShowAllMissing,
  onIncludeLockedAccounts,
  onMissingFrom,
  onMissingTo,
  onBatch,
  onOffset,
  onSlaWeeks,
  onCategory,
  onIncludeOutOfStock,
  onSelectBatch,
  onToggleAllShown,
  onOpenCards,
}: ParityQueueProps) {
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  // The batch can only take rows the page actually loaded.
  // The window Offset + # of Orders describes, clamped to what the list actually holds.
  const selectable = Math.max(0, Math.min(batch, rows.length - offset));
  const selectedIds = new Set(selection);
  const selectedHere = rows.filter((row) => selectedIds.has(row.orderId)).length;

  const tabs = (
    <div className="flex gap-1">
      {PACKING_TABS.map((option) => (
        <Button
          key={option.value}
          size="sm"
          variant={tab === option.value ? "secondary" : "ghost"}
          onClick={() => onTab(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );

  if (tab === "missing") {
    return (
      <div className="flex flex-col gap-4">
        {tabs}
        {missingCardsFailed ? null : (
          <MissingCards
            lines={missingCards?.lines ?? []}
            grouped={missingCards?.grouped ?? []}
            scanTruncated={missingCards?.scanTruncated ?? false}
            isPending={missingCardsPending}
            showAll={showAllMissing}
            includeLocked={includeLockedAccounts}
            from={missingFrom}
            to={missingTo}
            onShowAll={onShowAllMissing}
            onIncludeLocked={onIncludeLockedAccounts}
            onFrom={onMissingFrom}
            onTo={onMissingTo}
            onGenerate={onGenerateMissingCards}
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {tabs}

      <div className="grid grid-cols-1 items-start gap-x-8 gap-y-4 rounded-lg border p-4 lg:grid-cols-[auto_auto_auto_1fr]">
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground" htmlFor="parity-cat">
            Category
            <select
              id="parity-cat"
              className="h-9 rounded-md border bg-transparent px-2 text-sm"
              value={category ?? ""}
              onChange={(event) => onCategory(event.target.value || undefined)}
            >
              <option value="">None</option>
              {categories.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>

          <div className="flex items-center gap-2">
            <Switch
              id="parity-oos"
              checked={includeOutOfStock}
              onCheckedChange={(next: boolean) => onIncludeOutOfStock(next)}
            />
            <label className="text-xs text-muted-foreground" htmlFor="parity-oos">
              Include Out Of Stock
            </label>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label
            className="flex items-center gap-2 text-xs text-muted-foreground"
            htmlFor="parity-from"
          >
            <span className="w-20">Start Date</span>
            <Input
              id="parity-from"
              className="w-56"
              type="datetime-local"
              value={from ?? ""}
              onChange={(event) => onFrom(event.target.value || undefined)}
            />
          </label>
          <label
            className="flex items-center gap-2 text-xs text-muted-foreground"
            htmlFor="parity-to"
          >
            <span className="w-20">End Date</span>
            <Input
              id="parity-to"
              className="w-56"
              type="datetime-local"
              value={to ?? ""}
              onChange={(event) => onTo(event.target.value || undefined)}
            />
          </label>
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <input
              id="parity-batch"
              type="checkbox"
              className="accent-primary"
              checked={batchSelected}
              onChange={(event) => onSelectBatch(event.target.checked)}
            />
            <label className="text-sm" htmlFor="parity-batch">
              Select {formatNumber(selectable)} Orders
              {offset > 0 ? (
                <span className="text-muted-foreground"> from #{formatNumber(offset + 1)}</span>
              ) : null}
            </label>
          </div>
          <div className="flex items-end gap-3">
            <NumberField
              id="parity-n"
              label="# of Orders"
              value={batch}
              min={0}
              max={200}
              onCommit={onBatch}
            />
            <NumberField
              id="parity-off"
              label="Offset"
              value={offset}
              min={0}
              // Past the loaded rows the window is empty and the batch checkbox does nothing,
              // with no way to tell why — so the control cannot be taken there.
              max={Math.max(0, rows.length - 1)}
              onCommit={onOffset}
            />
            <NumberField
              id="parity-sla"
              label="SLA"
              value={slaWeeks}
              min={0}
              max={52}
              onCommit={onSlaWeeks}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 lg:items-end">
          <Button disabled={selection.length === 0} onClick={onOpenCards}>
            Open Combined Card View
          </Button>
          <Button variant="secondary" disabled={selection.length === 0} onClick={onOpenInTabs}>
            {OPEN_IN_TABS_LABEL}
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10 text-center">
                <input
                  aria-label="Select every order on this page"
                  type="checkbox"
                  className="accent-primary"
                  checked={allShownSelected}
                  ref={(node) => {
                    // Some-but-not-all shows a dash, which is how Retool marks a partial batch.
                    if (node) node.indeterminate = selectedHere > 0 && selectedHere < rows.length;
                  }}
                  // Adds or removes exactly the rows on screen. It used to call the batch
                  // handler, so it selected only the batch window — never reaching checked — and
                  // unchecking discarded rows ticked on another tab.
                  onChange={onToggleAllShown}
                />
              </TableHead>
              {COLUMNS.map((column) => (
                <TableHead key={column} className="text-center font-medium">
                  {column}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isPending
              ? ["a", "b", "c", "d", "e"].map((key) => (
                  <TableRow key={key}>
                    <TableCell colSpan={COLUMNS.length + 1}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              : rows.map((row) => (
                  <TableRow
                    key={row.orderId}
                    data-state={selectedIds.has(row.orderId) ? "selected" : undefined}
                    className={
                      selectedIds.has(row.orderId)
                        ? "bg-primary/10 text-center hover:bg-primary/15"
                        : "text-center"
                    }
                  >
                    <TableCell className="py-3 text-center">
                      <input
                        aria-label={`Select order ${row.tenantReferenceId ?? row.orderId}`}
                        type="checkbox"
                        className="accent-primary"
                        checked={selectedIds.has(row.orderId)}
                        onChange={() => onToggleRow(row.orderId)}
                      />
                    </TableCell>
                    <TableCell className="py-3 text-center">{when(row.createdAt)}</TableCell>
                    <TableCell className="py-3 text-center">
                      {dashIfEmpty(row.shopperName)}
                    </TableCell>
                    <TableCell className="py-3 text-center">
                      <div className="flex flex-wrap justify-center gap-1">
                        <Badge className={SUB_STATUS_BADGE}>
                          {SUB_STATUS_LABEL[row.subStatus] ?? row.subStatus}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="py-3 text-center">{when(row.updatedAt)}</TableCell>
                    <TableCell className="py-3 text-center">
                      {row.inStock ? <Check className="mx-auto size-4 text-primary" /> : null}
                    </TableCell>
                    <TableCell className="py-3 text-center">
                      {formatNumber(row.lineCount)}
                    </TableCell>
                    <TableCell className="py-3 text-center">
                      <div className="flex flex-wrap justify-center gap-1">
                        {row.categories.map((value) => (
                          <Badge
                            key={value}
                            className={CATEGORY_BADGE[value] ?? CATEGORY_BADGE_FALLBACK}
                          >
                            {value}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[16rem] truncate py-3 text-center text-sm">
                      <OrderReference row={row} />
                    </TableCell>
                    <TableCell className="py-3 text-center">
                      {row.isSlowShip ? <Check className="mx-auto size-4 text-primary" /> : null}
                    </TableCell>
                  </TableRow>
                ))}
            {!isPending && rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length + 1}
                  className="text-center text-muted-foreground"
                >
                  No orders match. "Include Out Of Stock" is off by default, as in Retool.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <Button variant="secondary" disabled={selection.length === 0} onClick={onExportSlips}>
          Export Packing Slips
        </Button>
        <Button variant="secondary" disabled={selection.length === 0} onClick={onExportPullSheet}>
          Export Pull Sheet
        </Button>
        <Button onClick={onGenerateMissingCards}>Generate Missing Card List</Button>
      </div>

      <p className="text-center text-sm text-muted-foreground">
        {selection.length > 0
          ? `${formatNumber(selection.length)} of ${formatNumber(total)} selected`
          : `${formatNumber(total)}${scanTruncated ? "+" : ""} results`}
        {rows.length < total ? ` (showing ${formatNumber(rows.length)})` : ""}
        {scanTruncated
          ? ` — the oldest ${formatNumber(PACKING_QUEUE_MAX_LIMIT)} are scanned, so the count is a floor and stock past them is approximate`
          : ""}
      </p>
    </div>
  );
}
